import { showFileAction } from "../../../utils/documents/show-file-action.ts"
import type { Attachment } from "../../Album/types/attachment.types.ts"
import type { ProjectDocument } from "../../Project/types/project-documents.types.ts"
import { fileFormatLabel } from "./file-format-label.ts"
import type { FileItem, FileRights, FileSource } from "../types/file-item.types.ts"

export const fileItemKey = (source: FileSource, id: string) => `${source}:${id}`

export const mapAttachmentToFileItem = (
  attachment: Attachment,
  rights: FileRights
): FileItem => {
  const canEdit = showFileAction({
    file: attachment,
    allowed: rights.editAllowed,
    foreignAllowed: rights.editForeignAllowed,
    currentUserId: rights.currentUserId
  })
  return {
    key: fileItemKey("album-attachment", attachment.id),
    id: attachment.id,
    source: "album-attachment",
    name: attachment.original_filename,
    fileUrl: attachment.file,
    mimeType: attachment.mime_type,
    fileTypeLabel: fileFormatLabel({
      name: attachment.original_filename,
      mimeType: attachment.mime_type
    }),
    fileSize: attachment.file_size,
    formattedFileSize: attachment.formatted_file_size,
    createdByName: attachment.created_by_name,
    created: attachment.created,
    updated: attachment.created,
    folderId: attachment.folder ?? null,
    canRename: canEdit,
    canMove: canEdit,
    canDelete: showFileAction({
      file: attachment,
      allowed: rights.deleteAllowed,
      foreignAllowed: rights.deleteForeignAllowed,
      currentUserId: rights.currentUserId
    }),
    documentType: attachment.document_type ?? null,
    documentTypeLabel: attachment.document_type_label ?? null,
    status: attachment.status ?? null,
    statusLabel: attachment.status_label ?? null,
    nextStatuses: attachment.next_statuses ?? [],
    deletedAt: attachment.deleted_at ?? null,
    deletedByName: attachment.deleted_by_name ?? null,
    customerUploaded: attachment.customer_uploaded ?? false,
    canShare: rights.manageOthersAllowed && !attachment.deleted_at,
    projectInfo: attachment.project_info ?? null,
    isCompanyScoped: attachment.is_company_scoped === true,
    raw: attachment
  }
}

// core.ProjectAttachment carries no owner, so its rights are the bare flags
// and there is no rename endpoint at all.
export const mapDocumentToFileItem = (
  document: ProjectDocument,
  rights: FileRights
): FileItem => ({
  key: fileItemKey("document", document.id),
  id: document.id,
  source: "document",
  name: document.file_name,
  fileUrl: document.file,
  fileTypeLabel: fileFormatLabel({ name: document.file_name }),
  fileSize: document.file_size ?? undefined,
  formattedFileSize: document.formatted_file_size ?? undefined,
  createdByName: document.uploaded_by,
  created: document.created,
  updated: document.updated,
  folderId: document.folder ?? null,
  canRename: false,
  canMove: rights.editAllowed,
  canDelete: rights.deleteAllowed,
  documentType: document.document_type ?? null,
  documentTypeLabel: document.document_type_label ?? null,
  status: document.status ?? null,
  statusLabel: document.status_label ?? null,
  nextStatuses: document.next_statuses ?? [],
  relatedRecord: document.related_record ?? null,
  projectInfo: document.project_info ?? null,
  isCompanyScoped: document.is_company_scoped === true,
  version: document.version,
  isCurrentVersion: document.is_current ?? true,
  canShare: rights.manageOthersAllowed && !document.deleted_at,
  deletedAt: document.deleted_at ?? null,
  deletedByName: document.deleted_by ?? null,
  customerUploaded: document.customer_uploaded ?? false,
  raw: document
})
